// Utilidades puras del correo (se prueban sin servidor): texto a HTML, asunto de respuesta, destinatarios, hilos.

export const escaparHtml = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

// [texto](https://...) -> enlace (firmas y plantillas). Recibe el texto ya escapado; solo http(s), mailto y tel.
export const conEnlaces = (html: string) => html.replace(/\[([^\]\n]+)\]\(((?:https?:\/\/|mailto:|tel:)[^\s)]+)\)/g, '<a href="$2">$1</a>');

// El motor recibe el cuerpo en HTML y lo sanea; aqui el texto del agente se convierte en parrafos.
export function textoAHtml(texto: string) {
  return texto
    .trim()
    .split(/\n{2,}/)
    .map((p) => `<p>${conEnlaces(escaparHtml(p)).replace(/\n/g, '<br>')}</p>`)
    .join('');
}

export const asuntoRespuesta = (asunto: string) => (/^\s*(re|rv|fw|fwd)\s*:/i.test(asunto) ? asunto.trim() : `Re: ${asunto.trim() || '(sin asunto)'}`);

const EMAIL = /^[^\s@<>(),;:"]+@[^\s@<>(),;:"]+\.[^\s@<>(),;:"]{2,}$/;

// "Ana <ana@x.co>, pedro@y.co; " -> { validos: ['ana@x.co','pedro@y.co'], invalidos: [] }
export function destinatarios(texto: string) {
  const validos: string[] = [];
  const invalidos: string[] = [];
  for (const parte of texto.split(/[,;\n]/)) {
    const t = parte.trim();
    if (!t) continue;
    const correo = (/<([^>]+)>/.exec(t)?.[1] ?? t).trim().toLowerCase();
    if (EMAIL.test(correo)) {
      if (!validos.includes(correo)) validos.push(correo);
    } else invalidos.push(t);
  }
  return { validos, invalidos };
}

// Quita del texto recibido lo citado de correos anteriores ("> ...", "El ... escribió:", "On ... wrote:", y el bloque
// de Outlook "From: / Sent: / To:" o "De: / Enviado: / Para:", que suele venir tras una raya de guiones bajos).
const ENCABEZADO_CITA = /^\s*\*?(From|De|Von)\s*:\*?\s+\S/i;
const FECHA_CITA = /^\s*\*?(Sent|Date|Enviado|Fecha|Gesendet)\s*:/i;
export function sinCitas(texto: string) {
  const lineas = texto.replace(/\r\n/g, '\n').split('\n');
  const out: string[] = [];
  for (let i = 0; i < lineas.length; i++) {
    const l = lineas[i];
    if (/^\s*(El|On|Le|Am)\b.{4,200}(escribió|wrote|a écrit|schrieb)\s*:\s*$/i.test(l)) break;
    if (/^-{2,}\s*(Mensaje original|Original Message)/i.test(l)) break;
    if (/^\s*_{10,}\s*$/.test(l) && lineas.slice(i + 1, i + 4).some((x) => ENCABEZADO_CITA.test(x))) break;
    if (ENCABEZADO_CITA.test(l) && lineas.slice(i + 1, i + 4).some((x) => FECHA_CITA.test(x))) break;
    if (/^\s*>/.test(l)) continue;
    out.push(l);
  }
  return out.join('\n').replace(/\n{3,}/g, '\n\n').trim();
}

// Quita la firma y los avisos legales del final de un correo recibido (D40): deja la despedida y el nombre, y corta lo
// demas (cargo, empresa, telefonos, logos, "Este mensaje es confidencial..."). Solo corta si lo que sigue a la despedida
// parece firma (lineas cortas); si despues hay parrafos largos, la despedida era parte del mensaje y no se toca.
const DESPEDIDA = /^\s*[*_]*\s*(saludos( cordiales)?|un saludo|cordial(es)? saludos?|cordialmente|atentamente|muchas gracias|gracias|quedo atent[oa]|quedamos atent[oa]s|best( regards)?|kind regards|regards|thanks|thank you|cheers|sincerely|warm regards)\b[\s,.!:]*[*_]*\s*$/i;
const AVISO_LEGAL = /(confidencial|confidential|aviso legal|disclaimer|antes de imprimir|please consider the environment|enviado desde mi|sent from my|get outlook for)/i;
// Un aviso en MAYUSCULAS al pie (p. ej. "NOTE: THERE HAS NOT BEEN ANY CHANGE IN ... BANK ACCOUNTS ...").
const enMayusculas = (l: string) => {
  const letras = l.replace(/[^\p{L}]/gu, '');
  return letras.length >= 30 && letras.replace(/\p{Lu}/gu, '').length / letras.length < 0.2;
};
const esAviso = (l: string) => AVISO_LEGAL.test(l) || enMayusculas(l);
export function sinFirma(texto: string) {
  const ls = texto.replace(/\r\n/g, '\n').split('\n');
  let corte = ls.length;
  for (let i = 1; i < ls.length; i++) {
    if (/^--\s*$/.test(ls[i]) || (AVISO_LEGAL.test(ls[i]) && ls[i].length < 400 && i >= 1)) {
      corte = i;
      break;
    }
    if (!DESPEDIDA.test(ls[i])) continue;
    // Nombre: la primera linea con texto despues de la despedida, si es corta.
    let j = i + 1;
    while (j < ls.length && !ls[j].trim()) j++;
    const conNombre = j < ls.length && ls[j].trim().length <= 40 && !/@|https?:|\d{5,}/.test(ls[j]) ? j + 1 : i + 1;
    const resto = ls.slice(conNombre).filter((l) => l.trim());
    if (resto.every((l) => l.length <= 90 || esAviso(l))) {
      corte = conNombre;
      break;
    }
  }
  return ls.slice(0, corte).join('\n').replace(/\n{3,}/g, '\n\n').trim();
}

// Lo que se muestra de un correo recibido: sin lo citado y sin la firma.
export const soloElMensaje = (texto: string) => sinFirma(sinCitas(texto)) || sinCitas(texto);

// A quien va una respuesta desde un mensaje de la conversacion. "Responder": a quien lo escribio (o, si fuimos
// nosotros, a quien le escribimos). "A todos": ademas los demas de Para y CC en copia. Nunca a nuestros propios buzones.
export type ModoRespuesta = 'responder' | 'todos' | 'reenviar' | 'nota';
export function destinosDe(m: { de: string; para: string[]; cc: string[] }, mios: string[], modo: ModoRespuesta) {
  if (modo === 'reenviar' || modo === 'nota') return { para: [] as string[], cc: [] as string[] };
  const norm = (h: string) => h.trim().toLowerCase();
  const propios = new Set(mios.map(norm));
  const ajenos = (xs: string[]) => [...new Set(xs.map(norm).filter((x) => x && !propios.has(x)))];
  const para = !propios.has(norm(m.de)) && m.de ? [norm(m.de)] : ajenos(m.para).slice(0, 1);
  const cc = modo === 'todos' ? ajenos([...m.para, ...m.cc]).filter((x) => !para.includes(x)) : [];
  return { para, cc };
}

export type ParticipanteBase = { role: string; handle: string; displayName?: string | null; personId?: string | null };
export type MensajeBase = { id: string; messageThreadId: string; receivedAt: string; participantes: ParticipanteBase[] };

// Agrupa mensajes por hilo, el hilo con el mensaje mas reciente primero.
export function agruparPorHilo<M extends MensajeBase>(mensajes: M[]) {
  const m = new Map<string, M[]>();
  for (const x of mensajes) m.set(x.messageThreadId, [...(m.get(x.messageThreadId) ?? []), x]);
  return [...m.entries()]
    .map(([id, ms]) => {
      const orden = [...ms].sort((a, b) => a.receivedAt.localeCompare(b.receivedAt));
      return { id, mensajes: orden, ultimo: orden[orden.length - 1] };
    })
    .sort((a, b) => b.ultimo.receivedAt.localeCompare(a.ultimo.receivedAt));
}

// Quien escribio un mensaje (FROM).
export const remitente = (m: { participantes: ParticipanteBase[] }) => m.participantes.find((p) => p.role === 'FROM');

// La otra persona de la conversacion: si el correo lo envie yo, a quien se lo envie; si no, quien lo envio.
export function contraparte(m: { participantes: ParticipanteBase[] }, mios: string[]) {
  const propios = new Set(mios.map((h) => h.toLowerCase()));
  const de = remitente(m);
  if (de && !propios.has(de.handle.toLowerCase())) return de;
  const orden = ['TO', 'CC', 'BCC'];
  const otros = m.participantes.filter((p) => p.role !== 'FROM' && !propios.has(p.handle.toLowerCase())).sort((a, b) => orden.indexOf(a.role) - orden.indexOf(b.role));
  return otros[0] ?? de ?? m.participantes[0];
}

// Carpetas que el CRM trae de un buzon: la de entrada y las de enviados. Borradores, spam, papelera y carpetas propias
// se quedan fuera (se pueden activar despues).
export const carpetaSeSincroniza = (nombre: string) => {
  const n = nombre.trim().toLowerCase();
  if (/(draft|borrador|spam|junk|trash|papelera|deleted|eliminad|ham|basura)/.test(n)) return false;
  return n === 'inbox' || n === 'bandeja de entrada' || /(^|[\s./])(sent|enviad)/.test(n);
};

// A quien va una respuesta. "Responder": a quien escribio (o, si fui yo, a quienes le escribi). "A todos": ademas
// los demas destinatarios en copia, sin mis buzones y sin repetir.
export function destinatariosRespuesta(m: { participantes: ParticipanteBase[] }, mios: string[], todos: boolean) {
  const propios = new Set(mios.map((h) => h.toLowerCase()));
  const norm = (h: string) => h.trim().toLowerCase();
  const de = remitente(m);
  const ajenos = (roles: string[]) => m.participantes.filter((p) => roles.includes(p.role) && p.handle && !propios.has(norm(p.handle))).map((p) => norm(p.handle));
  const para = de && !propios.has(norm(de.handle)) ? [norm(de.handle)] : ajenos(['TO']).slice(0, 1);
  const cc = todos ? [...new Set(ajenos(['TO', 'CC']))].filter((h) => !para.includes(h)) : [];
  return { para, cc };
}

export const asuntoReenvio = (asunto: string) => (/^\s*(rv|fw|fwd)\s*:/i.test(asunto) ? asunto.trim() : `RV: ${asunto.trim() || '(sin asunto)'}`);

export function textoReenvio(m: { asunto: string; texto: string; receivedAt: string; participantes: ParticipanteBase[] }) {
  const de = remitente(m);
  const para = m.participantes.filter((p) => p.role === 'TO').map((p) => p.handle).join(', ');
  const fecha = new Date(m.receivedAt).toLocaleString('es-CO', { dateStyle: 'long', timeStyle: 'short' });
  return `\n\n---------- Mensaje reenviado ----------\nDe: ${de ? `${de.displayName ? `${de.displayName} ` : ''}<${de.handle}>` : ''}\nFecha: ${fecha}\nAsunto: ${m.asunto}\nPara: ${para}\n\n${m.texto.trim()}`;
}

/* ---------------- vistas de la bandeja (como Outlook) ---------------- */

export type Vista = 'entrada' | 'noleidos' | 'destacados' | 'enviados' | 'archivados';
export type EstadoHilo = { leidoEn: string | null; destacado: boolean; archivado: boolean };
type HiloBase = { id: string; ultimo: { receivedAt: string; participantes: ParticipanteBase[] } };

const lomio = (m: { participantes: ParticipanteBase[] }, mios: string[]) => mios.includes((remitente(m)?.handle ?? '').toLowerCase());

// No leido: el ultimo mensaje no es mio y llego despues de la ultima vez que abri el hilo.
export const noLeido = (h: HiloBase, e: EstadoHilo | undefined, mios: string[]) => !lomio(h.ultimo, mios) && (!e?.leidoEn || e.leidoEn < h.ultimo.receivedAt);

export function filtrarVista<H extends HiloBase>(hilos: H[], estados: Map<string, EstadoHilo>, mios: string[], vista: Vista) {
  return hilos.filter((h) => {
    const e = estados.get(h.id);
    if (vista === 'archivados') return !!e?.archivado;
    if (e?.archivado) return false;
    if (vista === 'noleidos') return noLeido(h, e, mios);
    if (vista === 'destacados') return !!e?.destacado;
    if (vista === 'enviados') return lomio(h.ultimo, mios);
    return true;
  });
}

/* ---------------- plantillas: variables y firma por buzon ---------------- */

export type DatosPlantilla = Record<string, string | number | null | undefined>;

// Reemplaza {{grupo.campo}} con los datos; lo que no se conoce queda vacio (nunca se envia "{{...}}" al cliente).
export const rellenar = (texto: string, datos: DatosPlantilla) => texto.replace(/\{\{\s*([a-zA-Z]+\.[a-zA-Z]+)\s*\}\}/g, (_, k: string) => String(datos[k] ?? '')).replace(/[ \t]+(\n|$)/g, '$1');

export function datosAgente(nombre: string, correo = '') {
  const [n, ...resto] = nombre.trim().split(/\s+/);
  return { 'agente.nombre': n ?? '', 'agente.apellido': resto.join(' '), 'agente.correo': correo };
}

// La firma de un buzon: la de la empresa para ese buzon, si no la personal, si no ninguna.
export function firmaDe(buzon: string, firmas: { buzon: string; cuerpo: string }[], personal: string) {
  const b = buzon.trim().toLowerCase();
  return firmas.find((f) => f.buzon.toLowerCase() === b)?.cuerpo || personal || '';
}

// Asunto de un correo que sale desde un caso: el del correo al que se responde (no el titulo del caso, que se puede
// renombrar) con "Re:"/"RV:", y el numero del caso como [Ticket#N] (asi lo reconoce casos-desde-correo, igual que
// Zammad) para que la respuesta del cliente vuelva al mismo caso. Se puede cambiar antes de enviar.
export function asuntoDeCaso(asuntoBase: string, numero: number | null, modo: 'responder' | 'todos' | 'reenviar' | 'nota', tituloCaso = '') {
  const base = (asuntoBase || tituloCaso).trim();
  const con = modo === 'reenviar' ? asuntoReenvio(base) : asuntoRespuesta(base);
  if (!numero || con.toLowerCase().includes(`[ticket#${numero}]`)) return con;
  return `${con.replace(/\s*\[#\d+\]\s*/, ' ').trim()} [Ticket#${numero}]`;
}
